export type ChannelAccountChannel =
  | 'WhatsApp'
  | 'Instagram'
  | 'Webchat'
  | 'Telegram'
  | 'Messenger'
  | 'TikTok'
  | 'Voice'
  | 'Sms';

/** Whether each channel has an enabled account with usable credentials. */
export type ChannelReadiness = Partial<Record<ChannelAccountChannel, boolean>>;

export interface ChannelAccountWhatsAppSettings {
  cloudApiBaseUrl: string;
  cloudApiVersion: string;
  whatsAppAccountId?: string | null;
  cloudPhoneNumberId: string;
  phoneNumber?: string | null;
  hasCloudAccessToken: boolean;
  hasCloudWebhookVerifyToken: boolean;
  hasCloudAppSecret: boolean;
  validateCloudSignature: boolean;
}

export interface WhatsAppPhoneNumberRegistration {
  cloudPhoneNumberId: string;
  success: boolean;
  registeredAtUtc: string;
}

export interface WhatsAppAppSubscription {
  whatsAppAccountId: string;
  success: boolean;
  updatedAtUtc: string;
}

export interface WhatsAppSubscribedApp {
  appId: string | null;
  appName: string | null;
  appLink: string | null;
}

export interface WhatsAppSubscribedApps {
  whatsAppAccountId: string;
  items: WhatsAppSubscribedApp[];
  count: number;
  retrievedAtUtc: string;
}

export interface ChannelAccountInstagramSettings {
  apiBaseUrl: string;
  apiVersion: string;
  instagramBusinessAccountId: string;
  instagramUsername?: string | null;
  defaultCommentReplyMode: string;
  hasPageAccessToken: boolean;
}

export interface UpdateChannelAccountLimitsRequest {
  maxWhatsApp?: number;
  maxInstagram?: number;
  maxWebchat?: number;
  maxTelegram?: number;
  maxMessenger?: number;
  maxTikTok?: number;
  maxVoice?: number;
  maxWhatsAppApp?: number;
  maxTikTokApp?: number;
  maxTotal?: number;
}

export interface ChannelAccountLimitsSettingsResponse {
  effective: ChannelAccountLimits;
  defaults: ChannelAccountLimits;
  hasOverrides: boolean;
}

export interface ChannelAccountCredentialStatus {
  hasAccessToken: boolean;
  hasWebhookVerifyToken: boolean;
  hasAppSecret: boolean;
  validateSignatureEnabled: boolean;
}

export interface ChannelAccount {
  id: string;
  channel: ChannelAccountChannel;
  externalAccountId: string;
  displayName: string;
  isEnabled: boolean;
  isDefault: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  credentialStatus?: ChannelAccountCredentialStatus | null;
  connectionKind?: 'cloud' | 'app' | null;
}

export type WhatsAppConnectionKind = 'cloud' | 'app';

export const formatChannelAccountOptionLabel = (account: Pick<ChannelAccount, 'channel' | 'displayName' | 'externalAccountId' | 'connectionKind'>): string => {
  const name = account.displayName || account.externalAccountId;
  if (account.channel === 'WhatsApp') {
    const kind = account.connectionKind === 'app' ? 'App' : 'Cloud';
    return `${kind} · ${name}`;
  }

  if (account.channel === 'TikTok') {
    const kind = account.connectionKind === 'app' ? 'App' : 'Business';
    return `${kind} · ${name}`;
  }

  return name;
};

export interface ChannelAccountLimits {
  maxWhatsApp: number;
  maxInstagram: number;
  maxWebchat: number;
  maxTelegram: number;
  maxMessenger: number;
  maxTikTok: number;
  maxVoice: number;
  maxWhatsAppApp: number;
  maxTikTokApp: number;
  maxTotal: number;
  usedWhatsApp: number;
  usedInstagram: number;
  usedWebchat: number;
  usedTelegram: number;
  usedMessenger: number;
  usedTikTok: number;
  usedVoice: number;
  usedWhatsAppApp: number;
  usedTikTokApp: number;
  usedTotal: number;
}

export interface ChannelAccountListResponse {
  items: ChannelAccount[];
  limits: ChannelAccountLimits;
}

export interface UpdateChannelAccountRequest {
  displayName?: string;
  isEnabled?: boolean;
  isDefault?: boolean;
}

export interface CreateWhatsAppAppChannelAccountRequest {
  displayName: string;
  isDefault?: boolean;
}

export interface WhatsAppAppStatusResponse {
  configured: boolean;
}

export interface WhatsAppAppSession {
  account: ChannelAccount;
  connectionStatus: string;
  qrCodeBase64?: string | null;
  linkedPhoneNumber?: string | null;
  lastQrAtUtc?: string | null;
  lastDisconnectReason?: string | null;
  evolutionConfigured: boolean;
}

export interface CreateTikTokAppChannelAccountRequest {
  displayName: string;
  padCode: string;
  isDefault?: boolean;
}

export interface TikTokAppStatusResponse {
  configured: boolean;
  recommendedSku?: string;
  monthlyPriceUsd?: number;
}

export interface TikTokAppSession {
  account: ChannelAccount;
  connectionStatus: string;
  padCode: string;
  deviceSecret: string;
  ingestUrl: string;
  lastHeartbeatAtUtc?: string | null;
  lastEventAtUtc?: string | null;
  watcherSource?: string | null;
}

export interface CreateWhatsAppChannelAccountRequest {
  displayName: string;
  cloudPhoneNumberId: string;
  phoneNumber?: string;
  whatsAppAccountId?: string;
  cloudAccessToken: string;
  cloudApiBaseUrl?: string;
  cloudApiVersion?: string;
  cloudWebhookVerifyToken?: string;
  cloudAppSecret?: string;
  validateCloudSignature?: boolean;
  isDefault?: boolean;
}

export interface UpdateWhatsAppChannelAccountCredentialsRequest {
  cloudPhoneNumberId?: string;
  phoneNumber?: string;
  whatsAppAccountId?: string;
  cloudAccessToken?: string;
  cloudApiBaseUrl?: string;
  cloudApiVersion?: string;
  cloudWebhookVerifyToken?: string;
  cloudAppSecret?: string;
  validateCloudSignature?: boolean;
}

export interface CreateInstagramChannelAccountRequest {
  displayName: string;
  instagramBusinessAccountId: string;
  pageAccessToken: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  instagramUsername?: string;
  defaultCommentReplyMode?: string;
  isDefault?: boolean;
}

export interface UpdateInstagramChannelAccountCredentialsRequest {
  instagramBusinessAccountId?: string;
  pageAccessToken?: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  instagramUsername?: string;
  defaultCommentReplyMode?: string;
}

export interface ChannelAccountTelegramSettings {
  apiBaseUrl: string;
  hasBotToken: boolean;
  hasWebhookSecretToken: boolean;
}

export interface ChannelAccountMessengerSettings {
  apiBaseUrl: string;
  apiVersion: string;
  pageId: string;
  hasPageAccessToken: boolean;
  hasWebhookVerifyToken: boolean;
  hasAppSecret: boolean;
  validateSignature: boolean;
}

export interface ChannelAccountTikTokSettings {
  apiBaseUrl: string;
  businessOpenId: string;
  appId?: string | null;
  hasAccessToken: boolean;
  hasAppSecret: boolean;
  validateSignature: boolean;
}

export interface CreateTelegramChannelAccountRequest {
  displayName: string;
  botToken: string;
  webhookSecretToken?: string;
  apiBaseUrl?: string;
  isDefault?: boolean;
}

export interface UpdateTelegramChannelAccountCredentialsRequest {
  botToken?: string;
  webhookSecretToken?: string;
  apiBaseUrl?: string;
}

export interface CreateMessengerChannelAccountRequest {
  displayName: string;
  pageId: string;
  pageAccessToken: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  webhookVerifyToken?: string;
  appSecret?: string;
  validateSignature?: boolean;
  isDefault?: boolean;
}

export interface UpdateMessengerChannelAccountCredentialsRequest {
  pageId?: string;
  pageAccessToken?: string;
  apiBaseUrl?: string;
  apiVersion?: string;
  webhookVerifyToken?: string;
  appSecret?: string;
  validateSignature?: boolean;
}

export interface CreateTikTokChannelAccountRequest {
  displayName: string;
  businessOpenId: string;
  accessToken: string;
  appId?: string;
  appSecret?: string;
  apiBaseUrl?: string;
  validateSignature?: boolean;
  isDefault?: boolean;
}

export interface UpdateTikTokChannelAccountCredentialsRequest {
  businessOpenId?: string;
  accessToken?: string;
  appId?: string;
  appSecret?: string;
  apiBaseUrl?: string;
  validateSignature?: boolean;
}

export interface VoiceAgentConnectorSettings {
  enabled: boolean;
  answerMode: string;
  answeredNumbers?: string | null;
  handoffTarget?: string | null;
  greeting?: string | null;
  businessHoursStart?: string | null;
  businessHoursEnd?: string | null;
  businessHoursTimeZone?: string | null;
  businessHoursWeekdays?: string | null;
  hasTwilioAccountSid: boolean;
  hasTwilioAuthToken: boolean;
  twilioFromNumber?: string | null;
  incomingWebhookUrl?: string | null;
  statusCallbackUrl?: string | null;
}

export interface ChannelAccountVoiceSettings {
  serverUrl: string;
  hasClientId: boolean;
  hasClientSecret: boolean;
  inboundEnabled: boolean;
  outboundEnabled: boolean;
  defaultCallerId?: string | null;
  provider: string;
  voiceAgent?: VoiceAgentConnectorSettings | null;
}

export interface CreateVoiceChannelAccountRequest {
  displayName: string;
  serverUrl: string;
  clientId: string;
  clientSecret: string;
  inboundEnabled?: boolean;
  outboundEnabled?: boolean;
  defaultCallerId?: string;
  isDefault?: boolean;
}

export interface UpdateVoiceChannelAccountCredentialsRequest {
  serverUrl?: string;
  clientId?: string;
  clientSecret?: string;
  inboundEnabled?: boolean;
  outboundEnabled?: boolean;
  defaultCallerId?: string;
  voiceAgent?: {
    enabled?: boolean;
    answerMode?: string;
    answeredNumbers?: string;
    handoffTarget?: string;
    greeting?: string;
    businessHoursStart?: string;
    businessHoursEnd?: string;
    businessHoursTimeZone?: string;
    businessHoursWeekdays?: string;
    twilioAccountSid?: string;
    twilioAuthToken?: string;
    twilioFromNumber?: string;
  };
}

export interface ChannelAccountSmsSettings {
  apiUrl: string;
  httpMethod: string;
  contentType: string;
  bodyTemplate?: string | null;
  authHeaderName?: string | null;
  hasAuthHeaderValue: boolean;
  basicAuthUsername?: string | null;
  hasBasicAuthPassword: boolean;
  senderName?: string | null;
  successContains?: string | null;
  outboundEnabled: boolean;
}

export interface CreateSmsChannelAccountRequest {
  displayName: string;
  apiUrl: string;
  httpMethod?: string;
  contentType?: string;
  bodyTemplate?: string;
  authHeaderName?: string;
  authHeaderValue?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  senderName?: string;
  successContains?: string;
  outboundEnabled?: boolean;
  isDefault?: boolean;
}

export interface UpdateSmsChannelAccountCredentialsRequest {
  apiUrl?: string;
  httpMethod?: string;
  contentType?: string;
  bodyTemplate?: string;
  authHeaderName?: string;
  authHeaderValue?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  senderName?: string;
  successContains?: string;
  outboundEnabled?: boolean;
}

export interface CallingConnectionTestResult {
  succeeded: boolean;
  message?: string | null;
  testedAtUtc: string;
}
